import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore, Notification } from '@flows.cash/core';
import { format, isValid, parseISO } from 'date-fns';
import { Bell, Check, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore as useCoreAuthStore } from '@flows.cash/core';

export function NotificationsDropdown() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { 
    notifications, 
    unreadCount,
    isLoading, 
    syncNotifications, 
    markAsRead 
  } = useNotificationStore();
  const { user: coreUser, isLoading: authLoading } = useCoreAuthStore();

  useEffect(() => {
    // Only sync notifications if we have an authenticated user and auth is not loading
    if (!authLoading && coreUser) {
      console.log('Syncing notifications for user:', coreUser.id);
      syncNotifications();
      
      // Set up a periodic sync every 30 seconds for real-time updates
      const intervalId = setInterval(() => {
        if (coreUser) {
          syncNotifications();
        }
      }, 30000);
      
      return () => clearInterval(intervalId);
    }
  }, [syncNotifications, coreUser, authLoading]);

  const handleMarkAsRead = async (notificationId: string) => {
    await markAsRead(notificationId);
  };

  const handleViewAll = () => {
    navigate('/app/notifications');
  };

  const getNotificationColor = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return 'text-[#439476] dark:text-[#439476] bg-[#439476]/10';
      case 'error':
        return 'text-[#E53935] dark:text-[#E53935] bg-[#E53935]/10';
      case 'warning':
        return 'text-[#FF9800] dark:text-[#FF9800] bg-[#FF9800]/10';
      default:
        return 'text-[#2962FF] dark:text-[#2962FF] bg-[#2962FF]/10';
    }
  };

  const getNotificationTypeLabel = (notification: Notification) => {
    // First try to find a specific notification type translation based on the title
    // Compare with known patterns:
    if (notification.title.includes('Transaction')) {
      if (notification.title.includes('Added')) return t('notifications.transactions.added.title');
      if (notification.title.includes('Updated')) return t('notifications.transactions.updated.title');
      if (notification.title.includes('Deleted')) return t('notifications.transactions.deleted.title');
      if (notification.title.includes('Import')) return t('notifications.transactions.imported.title');
      if (notification.title.includes('Export')) return t('notifications.transactions.exportSuccess.title');
      return t('notifications.transactions.title', 'Transaction');
    }
    
    if (notification.title.includes('Account')) {
      if (notification.title.includes('Added')) return t('notifications.accounts.added.title');
      if (notification.title.includes('Updated')) return t('notifications.accounts.updated.title');
      if (notification.title.includes('Deleted')) return t('notifications.accounts.deleted.title');
      return t('notifications.accounts.title', 'Account');
    }
    
    if (notification.title.includes('Budget')) {
      if (notification.title.includes('Added')) return t('notifications.budget.added.title');
      if (notification.title.includes('Updated')) return t('notifications.budget.updated.title');
      if (notification.title.includes('Deleted')) return t('notifications.budget.deleted.title');
      if (notification.title.includes('Warning')) return t('notifications.budget.warning.title');
      if (notification.title.includes('Exceeded')) return t('notifications.budget.exceeded.title');
      return t('notifications.budget.title', 'Budget');
    }
    
    if (notification.title.includes('Goal') || notification.title.includes('Saving')) {
      if (notification.title.includes('Added')) return t('notifications.savingGoals.added.title');
      if (notification.title.includes('Completed') || notification.title.includes('Achieved')) {
        return t('notifications.savingGoals.completed.title');
      }
      if (notification.title.includes('Progress')) return t('notifications.savingGoals.progress.title');
      return t('notifications.savingGoals.title', 'Saving Goal');
    }
    
    // Default to the basic notification type
    return t(`notifications.types.${notification.type}`);
  };

  const getNotificationTranslation = (notification: Notification) => {
    // Direct translation approach - simply use i18next to translate
    // This assumes the notification title and message are already the translation keys
    
    // Check if notification title is a translation key
    if (notification.title && notification.title.includes('notifications.')) {
      try {
        const translatedTitle = t(notification.title, notification.data as Record<string, string>);
        const translatedMessage = notification.message?.includes('notifications.') 
          ? t(notification.message, notification.data as Record<string, string>)
          : notification.message;
          
        return {
          title: translatedTitle as string,
          message: translatedMessage as string
        };
      } catch (error) {
        console.error('Translation error:', error);
      }
    }
    
    // If not a translation key or there was an error, return the original content
    return {
      title: notification.title,
      message: notification.message
    };
  };

  // Helper function to safely format date
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return t('notifications.unknownTime', 'Unknown time');
    
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return t('notifications.unknownTime', 'Unknown time');
      return format(date, 'MMM d, h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error);
      return t('notifications.unknownTime', 'Unknown time');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="text-[#1C1C1C] dark:text-[#E8E8E8] hover:bg-[#439476]/10 hover:text-[#439476] rounded-xl"
          >
            <Bell className="h-5 w-5" />
          </Button>
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-[#E53935] text-white text-xs flex items-center justify-center z-10">
              {unreadCount}
            </span>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 rounded-xl border border-[#2F2F2F]/10 bg-white/80 dark:border-[#333840] dark:bg-[#182438]/80 backdrop-blur-xl backdrop-saturate-150 shadow-sm"
      >
        <div className="flex items-center justify-between px-6 py-3 border-b border-[#2F2F2F]/10 dark:border-[#333840]">
          <h3 className="font-semibold text-[#1C1C1C] dark:text-[#E8E8E8]">{t('notifications.title')}</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleViewAll}
            className="text-sm text-[#439476] hover:text-[#387565] hover:bg-[#439476]/10 rounded-lg h-9"
          >
            {t('notifications.viewAll')}
          </Button>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#439476] mx-auto mb-2"></div>
              <p className="text-[#A3A3A3]">{t('notifications.loading')}</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <BellOff className="h-8 w-8 mx-auto text-[#A3A3A3] mb-2" />
              <p className="text-[#A3A3A3]">{t('notifications.empty')}</p>
            </div>
          ) : (
            notifications.slice(0, 5).map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={cn(
                  "px-4 py-3 cursor-default rounded-lg mx-2 my-1 focus:bg-[#439476]/10 focus:text-[#439476]",
                  !notification.is_read && "border-l-4 border-l-[#439476] bg-white/50 dark:bg-[#182438]/50"
                )}
              >
                <div className="w-full">
                  <div className="flex items-center justify-between mb-1">
                    <span className={cn("text-xs font-medium px-2 py-1 rounded-full", getNotificationColor(notification.type))}>
                      {getNotificationTypeLabel(notification)}
                    </span>
                    <span className="text-xs text-[#A3A3A3]">
                      {formatDate(notification.created_at)}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8] mb-1">
                    {getNotificationTranslation(notification).title}
                  </p>
                  <p className="text-sm text-[#666666] dark:text-[#A3A3A3] line-clamp-2">
                    {getNotificationTranslation(notification).message}
                  </p>
                  {!notification.is_read && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification.id);
                      }}
                      className="mt-2 text-xs text-[#439476] hover:text-[#387565] hover:bg-[#439476]/10 rounded-lg h-8"
                    >
                      <Check className="h-3 w-3 mr-1" />
                      {t('notifications.markAsRead')}
                    </Button>
                  )}
                </div>
              </DropdownMenuItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
} 